// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react'
import styled from 'styled-components/macro'
import { isNodeJsEnv, getLocaleStr, codeToLocale, normalizeFileNameStr, getStoreName } from 'utils/utils'
import { isEmpty } from 'lodash-es'
import SearchStore from 'static/components/StoreLocator/SearchStore'
import Map from 'static/components/StoreLocator/Map'
import FullStoreList from 'static/components/StoreLocator/FullStoreList'
import List from 'static/components/StoreLocator/List'
import SmartLink from 'static/components/SmartLink'
import infra from 'mobx/Infra'
import mobileApplication from 'mobx/MobileApplication'
import user from 'mobx/User'
import home from 'mobx/Home'
import Skeleton from 'components/Skeleton'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { MapContainer, MapSection } from '../components/StoreLocator/common'
import Title from 'static/components/StoreLocator/common/Title'
import { GOOGLE_API_KEY, Page } from 'utils/constants'
import { getAllChainStores, getLocale, getLogo, getChainStoresByAddress } from 'static/components/StoreLocator/common/requestUtils'
import ChainShutDownPage from './errors/ChainShutDownPage'
import { sendCustomEvent } from '../../utils/analytics/analytics'
import { StoreContext } from '../../contexts/StoreContext'
import { useRouter } from 'next/router'
import { useStores } from 'hooks/useStores'

if (!isNodeJsEnv) {
	/**
	 * Currently css/scss files MUST be imported dynamically in order not to crash the node js server react rendering
	 * TODO-SERGEY: the long term solution is https://coderedirect.com/questions/239528/how-can-i-use-style-loader-in-react-ssr-server-side-rendering
	 * Do NOT remove the comment inside the import! for more info: https://webpack.js.org/api/module-methods/#magic-comments
	 */
	import(/* webpackMode: "eager" */ 'react-tippy/dist/tippy.css')
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 1280px;
	margin: 50px auto 20px auto;
	padding: 0 40px;

	@media (max-width: 1280px) {
		width: 100%;
		margin: 0;
		padding: 30px 15px 15px 15px;
	}
`

const SearchStoreContainer = styled.div`
	margin-inline-start: 13px;
	width: 380px;
	grid-area: search;

	@media (max-width: 576px) {
		margin: 0;
		width: 100%;
	}
`

// demos styles
const CommonStyles = styled.div`
	body {
		background-color: black;
	}
`

const StyledLoadingSpinner = styled.div`
	position: absolute;
	z-index: 9999999;
	width: 100vw;
	height: 100vh;
	background-color: #0006;
	display: flex;
	flex-flow: column nowrap;
	justify-content: center;
	align-items: center;
`

const StoreLocator = (props) => {
	const { isGeneratedStatically = false, lang, chainId, chainStores, conversationText, chainCountryLocation, footer, params } = props
	const [locale, setLocale] = useState({})
	const [logo, setLogo] = useState({})
	const [stores, setStores] = useState([]) // Will contain all (non-shutdown) stores in branch
	const [activeStores, setActiveStores] = useState([]) // Will contain only the active (allows delivery or pick-up) stores
	const [currentLocation, setCurrentLocation] = useState()
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [hasStores, setStoresFound] = useState()
	const router = useRouter()
	const [isLoading, setIsLoading] = useState(true)
	const [selectedPlace, setSelectedPlace] = useState('')
	const [isSearchStoreMounted, setIsSearchStoreMounted] = useState(false)
	const { store, setStore } = useContext(StoreContext)
	const { cartStore, addressManagerV2Store } = useStores()

	const onSelectedStore = useCallback(
		(currStore) => {
			const storeName = getLocaleStr(currStore.title, codeToLocale[lang] || 'en')
			SmartLink.route(`/${lang}/store-locator/${normalizeFileNameStr(storeName)}`, router)
		},
		[router, lang]
	)

	const onPlaceSelected = useCallback(
		async (place) => {
			setIsLoading(true)

			try {
				if (place?.geometry?.location) {
					const placeAddress = {
						formatted: place.formatted_address,
						latLng: place?.geometry?.location?.toJSON(),
						addressVal: place.formatted_address,
					}
					const storesRes = await getChainStoresByAddress(chainId, placeAddress, lang, params)

					if (!storesRes.error) {
						const storesWithRank = storesRes.msg.map((currStore, index) => ({ ...currStore, storeRank: index + 1 }))
						setStores(storesWithRank)
						// This will update list, but not the map - which is intended, for this version.
						setActiveStores(storesRes.msg)
						setStoresFound(true)
						setSelectedPlace(place.formatted_address)
					} else {
						setStores([])
					}
					setCurrentLocation(place.geometry.location.toJSON())
				}
			} catch (err) {
				console.error(err)
			} finally {
				setIsLoading(false)
			}
		},
		[chainId, lang, params]
	)

	const init = useCallback(async () => {
		setIsLoading(true)

		setStores(chainStores.msg)

		const localeRes = await getLocale(lang, params)

		if (!localeRes.error) {
			setLocale(localeRes)
		}

		const logoRes = getLogo(lang, params)

		if (!logoRes.error) {
			setLogo(logoRes)
		}

		try {
			const chainStoresRes = await getAllChainStores(lang, params)
			// msg is an array of active stores which have pickup and/or delivery allowed
			if (!chainStoresRes.error && chainStoresRes.msg) {
				setActiveStores(chainStoresRes.msg)
			} else {
				if (chainStoresRes.error) {
					console.warn(`Error while getting active stores: ${chainStoresRes.error}`)
				} else {
					console.warn('Returned active stores not in right format')
				}
				setActiveStores([])
			}
		} catch (err) {
			console.error(err)
			setActiveStores([])
		} finally {
			setIsLoading(false)
		}
	}, [chainStores.msg, lang, params])

	const onSearchReset = useCallback(() => {
		setStoresFound(false)
		init()
	}, [init])

	useEffect(() => {
		init()
	}, [init])

	const onStartOrderButtonClick = useCallback(async () => {

		// On new localization flow, we don't do anything since this method is not called in Skeleton (SubHeaderGroup not rendered)
		if(addressManagerV2Store.newLocalizationFlowEnabled) {
			return
		}

		if (addressManagerV2Store.isUserLocalized() && !home.areAllStoresClosed()) {
			const { menuRes, storeMetaData, serverSession, menuUrl } = await addressManagerV2Store.onMenuClickWhenLocalized(cartStore, user)
			await user.setSession(serverSession)

			setStore((prevStore: any) => ({ ...prevStore, data: menuRes, metaData: storeMetaData }))

			const orderType = addressManagerV2Store.getLocalizedOrderType()
			const localeStoreName = getStoreName(store, orderType, home.locale.msg, addressManagerV2Store)

			sendCustomEvent({
				category: 'Store locator order now button',
				event: 'Store locator order now button',
				action: 'button clicked',
				label: localeStoreName,
			})

			if (router) {
				router.push(`${menuUrl}`)
			} else {
				window.location.replace(`${window.location.origin}${menuUrl}`)
			}
		} else {
			router.push(`/`)
		}
	}, [router, setStore, store])

	const staticPage = useMemo(
		() => (
			<CommonStyles>
				<Skeleton
					page={Page.STORE_LOCATOR}
					showLogo
					showCustomTitle
					showHeader
					isPopupOpen={isModalOpen}
					onClosePopup={() => setIsModalOpen(false)}
					conversationText={conversationText}
					onStartOrderButtonClick={onStartOrderButtonClick}
				>
					{isLoading && (
						<StyledLoadingSpinner>
							<LoadingSpinner chainId={chainId} brand={params.brand} />
						</StyledLoadingSpinner>
					)}
					<Container>
						<MapSection>
							{!isEmpty(locale) ? (
								<SearchStoreContainer>
									<SearchStore
										apiKey={locale.key?.trim() || GOOGLE_API_KEY}
										region={locale.region}
										conversationText={conversationText}
										selectedPlace={selectedPlace}
										onPlaceSelected={onPlaceSelected}
										onSearchReset={onSearchReset}
										setIsSearchStoreMounted={setIsSearchStoreMounted}
									/>
								</SearchStoreContainer>
							) : (
								<Title variant="h1">{conversationText.eCommerce.storeLocator.findYourNearestStore}</Title>
							)}

							{!isEmpty(stores) && (
								<List
									stores={stores}
									updatedStores={activeStores}
									locale={locale.msg || codeToLocale[lang]}
									onSelectedStore={onSelectedStore}
									conversationText={conversationText}
									infra={infra}
									mobileApplication={mobileApplication}
									isGeneratedStatically={isGeneratedStatically}
									setModalOpen={setIsModalOpen}
									params={params}
									hasStores={hasStores}
									assets={params.assets}
									setIsLoading={setIsLoading}
									user={user}
								/>
							)}

							<MapContainer id="map-container" data-testid="store-locator-map">
								{isSearchStoreMounted && (
									<Map
										locale={locale}
										stores={stores}
										currentLocation={currentLocation}
										onSelectedStore={onSelectedStore}
										logo={logo}
										setStores={setStores}
										setCurrentLocation={setCurrentLocation}
										chainId={chainId}
										lang={lang}
										defaultLocation={chainCountryLocation.latLng}
										hasStores={hasStores}
										assets={params.assets}
										conversationText={conversationText}
									/>
								)}
							</MapContainer>
						</MapSection>
						<FullStoreList
							chainStores={chainStores}
							locale={codeToLocale[lang] || 'en_US'}
							onSelectedStore={onSelectedStore}
							conversationText={conversationText}
						/>
					</Container>
				</Skeleton>
			</CommonStyles>
		),
		[
			activeStores,
			chainCountryLocation.latLng,
			chainId,
			chainStores,
			conversationText,
			currentLocation,
			footer,
			hasStores,
			isGeneratedStatically,
			isLoading,
			isModalOpen,
			isSearchStoreMounted,
			lang,
			locale,
			logo,
			onPlaceSelected,
			onSearchReset,
			onSelectedStore,
			onStartOrderButtonClick,
			params,
			selectedPlace,
			stores,
		]
	)

	return chainStores.error || (activeStores.length === 0 && !hasStores && !isLoading) ? <ChainShutDownPage /> : staticPage
}

export default StoreLocator
